import type { LiveSubscriptionRow } from '../../services/billingApi';

/**
 * THE ACCOUNT'S BILL, ONE STORE AT A TIME.
 *
 * A merchant sets payment up per location, so a figure for the whole account
 * ($880 across three stores and their add-ons) on an order for ONE store reads
 * as that store's bill — and scares them. Every money panel on the order screen
 * groups through here so the Paid stage, the subscription list and the
 * transactions can never disagree about which store a dollar belongs to.
 *
 * Pure: no fetching, so it is the same answer wherever it is asked.
 */

export const isAddOn = (s: Pick<LiveSubscriptionRow, 'source'>) => s.source === 'addon_order';

export interface StoreBill {
  /** Null for subscriptions Stripe could not place at any store. */
  locationId: string | null;
  name: string;
  /** Store plan first, then add-ons. */
  subs: LiveSubscriptionRow[];
  storePlanCents: number;
  addOnCents: number;
  monthlyCents: number;
}

const cents = (s: LiveSubscriptionRow) => s.monthlyCents || 0;

/**
 * Group live subscriptions by store. The order's own store comes first, then
 * the others by name, and anything unplaced last — it is the one to fix.
 */
export function groupByStore(
  subs: readonly LiveSubscriptionRow[],
  names: Record<string, string>,
  firstLocationId?: string | null,
): StoreBill[] {
  const by = new Map<string, LiveSubscriptionRow[]>();
  for (const s of subs) {
    const k = s.locationId || '';
    if (!by.has(k)) by.set(k, []);
    by.get(k)!.push(s);
  }
  const bills = Array.from(by.entries()).map(([k, list]): StoreBill => {
    const ordered = [...list].sort((a, b) => Number(isAddOn(a)) - Number(isAddOn(b)));
    const storePlanCents = ordered.filter((s) => !isAddOn(s)).reduce((n, s) => n + cents(s), 0);
    const addOnCents = ordered.filter(isAddOn).reduce((n, s) => n + cents(s), 0);
    return {
      locationId: k || null,
      name: k ? (names[k] || k) : 'Not linked to a store',
      subs: ordered,
      storePlanCents,
      addOnCents,
      monthlyCents: storePlanCents + addOnCents,
    };
  });
  const rank = (b: StoreBill) => (b.locationId && b.locationId === firstLocationId ? 0 : b.locationId ? 1 : 2);
  return bills.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/**
 * WHICH STORE AN INVOICE BELONGS TO.
 *
 * billing-service stamps `locationId` on the invoices it raises; a renewal
 * Stripe raises on its own carries none, so it is placed by the subscription
 * that raised it. Null is a one-time invoice we cannot place.
 */
export function invoiceLocationId(
  inv: { locationId?: string | null; subscription?: string | null },
  subs: readonly LiveSubscriptionRow[],
): string | null {
  if (inv.locationId) return inv.locationId;
  if (inv.subscription) return subs.find((s) => s.subscriptionId === inv.subscription)?.locationId || null;
  return null;
}

/**
 * THE SUBSCRIPTION THIS ORDER STARTED, if any.
 *
 * An add-on order names its own; failing that, an add-on subscription that
 * says it bills this order.
 */
export function orderSubscription(
  subs: readonly LiveSubscriptionRow[],
  order: { orderId: string; addOnSubscriptionId?: string },
): LiveSubscriptionRow | undefined {
  return subs.find((s) => order.addOnSubscriptionId && s.subscriptionId === order.addOnSubscriptionId)
    || subs.find((s) => isAddOn(s) && s.addOnOrderId === order.orderId);
}

/**
 * BEFORE THIS ORDER, WHAT IT ADDS, AND THE STORE'S NEW MONTHLY.
 *
 * "Before" is the store today less this order's subscription — the store's
 * bill as it stood when the order was placed, for any order that started its
 * own subscription.
 */
export function orderDelta(store: StoreBill, orderSub: LiveSubscriptionRow | undefined) {
  if (!orderSub) return null;
  const adds = cents(orderSub);
  return { beforeCents: store.monthlyCents - adds, addsCents: adds, nowCents: store.monthlyCents };
}

/** First day of the month `n` months after `from`. */
const monthStart = (from: Date, n: number) => new Date(from.getFullYear(), from.getMonth() + n, 1);

export interface ForecastRow { month: Date; storePlanCents: number; addOnCents: number; totalCents: number }

/**
 * THE NEXT TWELVE MONTHS FOR ONE STORE.
 *
 * Each subscription at what Stripe bills it today. The one thing that is known
 * to change is this order's hardware: its rows stop when its term runs out, so
 * a 24-month lease does not read as forever.
 */
export function storeForecast(
  store: StoreBill,
  opts: { orderSub?: LiveSubscriptionRow; orderStartedAt?: number; orderTermMonths?: number; from?: Date } = {},
): ForecastRow[] {
  const from = opts.from || new Date();
  const ends = opts.orderSub && opts.orderStartedAt && opts.orderTermMonths
    ? monthStart(new Date(opts.orderStartedAt), opts.orderTermMonths)
    : null;
  return Array.from({ length: 12 }, (_, i) => {
    const month = monthStart(from, i);
    const dropped = ends && month >= ends ? cents(opts.orderSub!) : 0;
    const addOnCents = store.addOnCents - dropped;
    return { month, storePlanCents: store.storePlanCents, addOnCents, totalCents: store.storePlanCents + addOnCents };
  });
}
